import Groq from 'groq-sdk';

// Inicializar cliente Groq
const groq = new Groq({
    apiKey: import.meta.env.VITE_GROQ_API_KEY,
    dangerouslyAllowBrowser: true, // Para uso en el navegador
});

/**
 * Servicio OCR usando Groq Vision API
 * Modelo: llama-3.2-11b-vision-preview (más económico y rápido)
 */

export interface OCRResult {
    success: boolean;
    text?: string;
    error?: string;
    tokenUsage?: {
        promptTokens: number;
        completionTokens: number;
        totalTokens: number;
    };
}

/**
 * Convierte una imagen/PDF a base64 para enviar a la API
 */
async function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = reader.result as string;
            // Remover el prefijo "data:image/...;base64," si existe
            const base64Data = base64.includes(',') ? base64.split(',')[1] : base64;
            resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

/**
 * Detecta si un PDF tiene texto extraíble (no escaneado)
 * Si el PDF ya tiene texto, devuelve el texto directamente
 */
async function extractTextFromPDF(file: File): Promise<string | null> {
    try {
        // Usaremos PDF.js para extraer texto
        // @ts-ignore - pdfjs-dist está disponible globalmente
        if (typeof window !== 'undefined' && window.pdfjsLib) {
            const arrayBuffer = await file.arrayBuffer();
            // @ts-ignore
            const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;

            let fullText = '';
            for (let i = 1; i <= pdf.numPages; i++) {
                const page = await pdf.getPage(i);
                const textContent = await page.getTextContent();
                const pageText = textContent.items.map((item: any) => item.str).join(' ');
                fullText += pageText + '\n';
            }

            // Si tiene al menos 50 caracteres, asumimos que es texto extraíble
            return fullText.trim().length > 50 ? fullText.trim() : null;
        }

        return null;
    } catch (error) {
        console.error('Error extrayendo texto de PDF:', error);
        return null;
    }
}

/**
 * Extrae texto de una imagen usando Groq Vision API (OCR)
 * @param file - Archivo de imagen (JPG, PNG, etc.)
 */
export async function extractTextFromImage(file: File): Promise<OCRResult> {
    try {
        console.log('🔍 Iniciando OCR con Groq Vision...');

        // Convertir imagen a base64
        const base64Image = await fileToBase64(file);

        // Determinar el tipo MIME
        const mimeType = file.type || 'image/jpeg';

        // Crear el data URL completo
        const imageUrl = `data:${mimeType};base64,${base64Image}`;

        // Llamar a Groq Vision API
        const completion = await groq.chat.completions.create({
            model: 'llama-3.2-11b-vision-preview', // Modelo más económico
            messages: [
                {
                    role: 'user',
                    content: [
                        {
                            type: 'text',
                            text: `Extract ALL text from this CV/resume image. 
              
Instructions:
- Extract ALL visible text exactly as it appears
- Maintain the structure and formatting
- Include names, contact info, work experience, education, skills, etc.
- Do not add comments or explanations
- Return ONLY the extracted text

Extracted text:`,
                        },
                        {
                            type: 'image_url',
                            image_url: {
                                url: imageUrl,
                            },
                        },
                    ],
                },
            ],
            temperature: 0.1, // Baja temperatura para precisión
            max_tokens: 2048, // Suficiente para un CV completo
        });

        const extractedText = completion.choices[0]?.message?.content || '';

        console.log('✅ OCR completado exitosamente');
        console.log(`📊 Tokens usados: ${completion.usage?.total_tokens || 0}`);

        return {
            success: true,
            text: extractedText.trim(),
            tokenUsage: {
                promptTokens: completion.usage?.prompt_tokens || 0,
                completionTokens: completion.usage?.completion_tokens || 0,
                totalTokens: completion.usage?.total_tokens || 0,
            },
        };
    } catch (error) {
        console.error('❌ Error en OCR:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Error desconocido en OCR',
        };
    }
}

/**
 * Función principal: Extrae texto de un CV (PDF o imagen)
 * - Si es PDF con texto: extrae directamente (gratis)
 * - Si es PDF escaneado o imagen: usa OCR con Groq Vision (tiene costo)
 */
export async function extractTextFromCV(file: File): Promise<OCRResult> {
    try {
        const fileType = file.type.toLowerCase();

        // Caso 1: PDF - Intentar extraer texto primero
        if (fileType === 'application/pdf') {
            console.log('📄 Detectado PDF, intentando extracción directa...');

            const pdfText = await extractTextFromPDF(file);

            if (pdfText) {
                console.log('✅ PDF tiene texto extraíble (sin costo de OCR)');
                return {
                    success: true,
                    text: pdfText,
                    tokenUsage: {
                        promptTokens: 0,
                        completionTokens: 0,
                        totalTokens: 0,
                    },
                };
            }

            console.log('⚠️ PDF es escaneado, requiere OCR...');
            // Si el PDF no tiene texto, necesitamos convertirlo a imagen
            // Por simplicidad, pediremos al usuario que use una imagen
            return {
                success: false,
                error: 'PDF escaneado detectado. Por favor, convierte el PDF a imagen (JPG/PNG) y vuelve a intentar.',
            };
        }

        // Caso 2: Imagen - Usar OCR directamente
        if (fileType.startsWith('image/')) {
            console.log('🖼️ Detectada imagen, usando OCR...');
            return await extractTextFromImage(file);
        }

        // Formato no soportado
        return {
            success: false,
            error: 'Formato de archivo no soportado. Usa PDF o imágenes (JPG, PNG)',
        };
    } catch (error) {
        console.error('Error en extractTextFromCV:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Error desconocido',
        };
    }
}

/**
 * Verifica si un texto extraído es suficiente (tiene contenido útil)
 */
export function hasValidText(text: string): boolean {
    const trimmed = text.trim();
    // Consideramos válido si tiene al menos 100 caracteres y contiene palabras
    return trimmed.length > 100 && /\w+/.test(trimmed);
}
